import { Module } from 'vuex'
import { RootState } from 'surface/xstore/types'
import { __ } from 'bits/intl'
import window from 'bits/global'
import debounce from 'lodash.debounce'

interface WallChannel extends ActionCable.Channel {
  received: (data: {}) => void
  disconnect: () => void
  present: () => void
  absent: () => void
}

interface DeviceChannel extends ActionCable.Channel {
  received: (data: {}) => void
}

interface RealtimeState {
  wallChannel: WallChannel | null
  deviceChannel: DeviceChannel | null
  dispatchQueue: any[]
  notificationUid: string | null
}

const defaultState = (): RealtimeState => ({
  wallChannel: null,
  deviceChannel: null,
  dispatchQueue: [],
  notificationUid: null,
})

function userNeedsFocus (rootGetters): boolean {
  const isNonChatPostBeingEdited = !rootGetters.isBackchannel && !!rootGetters['post/postBeingEditedCid']
  return isNonChatPostBeingEdited || !!rootGetters['reaction/postIdBeingReactedTo'] || rootGetters['comment/isCommenting']
}

const RealtimeModule: Module<RealtimeState, RootState> = {
  namespaced: true,
  state: defaultState,
  getters: {
    areUpdatesPending (state): boolean {
      return state.dispatchQueue.length > 0
    },
  },
  mutations: {
    SET_WALL_CHANNEL (state, wallChannel): void {
      state.wallChannel = wallChannel
    },
    SET_DEVICE_CHANNEL (state, deviceChannel): void {
      state.deviceChannel = deviceChannel
    },
    QUEUE_UPDATE_ACTION (state, action): void {
      state.dispatchQueue.push(action)
    },
    CLEAR_DISPATCH_QUEUE (state): void {
      state.dispatchQueue = []
    },
    SET_NOTIFICATION_UID (state, uid): void {
      state.notificationUid = uid
    },
    CLEAR_NOTIFICATION_UID (state): void {
      state.notificationUid = null
    },
  },
  actions: {
    connect ({ dispatch }, cable): void {
      dispatch('subscribeWallChannel', cable)
      dispatch('subscribeDeviceChannel', cable)
      dispatch('subscribeToStoreChanges')
    },

    disconnect ({ state }): void {
      state.wallChannel && state.wallChannel.unsubscribe()
      state.deviceChannel && state.deviceChannel.unsubscribe()
    },

    absent ({ state }): void {
      state.wallChannel && state.wallChannel.present()
    },

    present ({ state }): void {
      state.wallChannel && state.wallChannel.absent()
    },

    subscribeWallChannel ({ dispatch, commit, rootGetters }, cable): void {
      const wallId = rootGetters.wallId
      const wallChannel = cable.subscriptions.create({ channel: 'WallChannel', wall_id: wallId }, {
        received (data): void { dispatch('handleMessage', data) },
        disconnect (): void { this.absent() },
        present (): void { this.perform('present', { wall_id: wallId }) },
        absent (): void { this.perform('absent', { wall_id: wallId }) },
      })
      commit('SET_WALL_CHANNEL', wallChannel)
    },

    subscribeDeviceChannel ({ dispatch, commit, rootState }, cable): void {
      const deviceChannel = cable.subscriptions.create({ channel: 'DeviceChannel', device_id: rootState.deviceId }, {
        received (data): void { dispatch('handleMessage', data) },
      })
      commit('SET_DEVICE_CHANNEL', deviceChannel)
    },

    subscribeToStoreChanges ({ dispatch, getters, rootGetters }): void {
      const flushUpdates = (): void => {
        if (getters.areUpdatesPending && !userNeedsFocus(rootGetters)) {
          dispatch('dispatchAllQueuedActions')
        }
      }
      const debouncedFlushUpdates = debounce(flushUpdates, 200, { leading: true })
      this.subscribe((): void => debouncedFlushUpdates())
    },

    handleMessage ({ dispatch, rootState }, messageObj): void {
      if (!messageObj || !messageObj.event || messageObj.uid === rootState.uid) return

      const payload = JSON.parse(messageObj.message)
      const dispatchNow = (type: string): Promise<any> => dispatch(type, payload, { root: true })
      const queue = (type: string): Promise<any> => dispatch('queueUpdateAction', { type, payload })
      // We add a delay if the post has an attachment, so that we have sufficient
      // lead time for request coalescing to come into play.
      // See original commit: https://github.com/padlet/mozart/commit/9f9e0d60f3934c0e1c6ef411af97e1e24f429151
      const delay = (type: string): void => {
        const upTo1andHalfSecDelay = (Math.random() + 0.5) * 1000
        payload.attachment ? setInterval((): void => { queue(type) }, upTo1andHalfSecDelay) : queue(type)
      }

      // Right now we are calling 'remote' actions in the modules themselves.
      // Eventually, the modules will have no concept of remote updates.
      // They'll only have simple CRUD operations that'll be called by this guy.
      const handlers = {
        edit_wall (): void { dispatchNow('updateWall') },
        delete_wall (): void { window.location.href = '/' },
        add_section (): void { dispatchNow('section/newSectionRemote') },
        edit_section (): void { dispatchNow('section/editSectionRemote') },
        delete_section (): void { dispatchNow('section/deleteSectionRemote') },
        add_wish (): void { delay('post/newPostRemote') },
        edit_wish (): void { delay('post/editPostRemote') },
        delete_wish (): void {
          dispatchNow('post/deletePostRemote')
          dispatchNow('postAction/deletePostRemote')
        },
        add_comment (): void { queue('comment/newCommentRemote') },
        edit_comment (): void { queue('comment/editCommentRemote') },
        delete_comment (): void { queue('comment/deleteCommentRemote') },
        add_reaction (): void { dispatchNow('reaction/newReactionRemote') },
        edit_reaction (): void { dispatchNow('reaction/editReactionRemote') },
        delete_reaction (): void { dispatchNow('reaction/deleteReactionRemote') },
        add_connection (): void { dispatchNow('postConnection/newConnectionRemote') },
        delete_connection (): void { dispatchNow('postConnection/deleteConnectionRemote') },
        login (): void { window.location.reload() },
        logout (): void { window.location.reload() },
      }

      const handler = handlers[messageObj.event]
      handler && handler()
    },

    queueUpdateAction ({ dispatch, commit, getters, rootGetters }, { type, payload }): void {
      if (userNeedsFocus(rootGetters)) {
        if (!getters.areUpdatesPending) dispatch('setUpdatesPendingNotification')
        commit('QUEUE_UPDATE_ACTION', { type, payload })
      } else {
        dispatch(type, payload, { root: true })
      }
    },

    setUpdatesPendingNotification ({ dispatch, commit }): void {
      dispatch('snackbars/setSnackbar', {
        message: __('Padlet updated elsewhere.'),
        actionText: __('Update'),
        actionName: 'realtime/dispatchAllQueuedActions',
        persist: true,
      }, { root: true }).then((uid): void => commit('SET_NOTIFICATION_UID', uid))
    },

    clearUpdatesPendingNotification ({ dispatch, commit, state },): void {
      dispatch('snackbars/clearSnackbar', state.notificationUid, { root: true })
      commit('CLEAR_NOTIFICATION_UID')
    },

    dispatchAllQueuedActions ({ dispatch, state }): void {
      state.dispatchQueue.forEach(({ type, payload }): Promise<any> => dispatch(type, payload, { root: true }))
      dispatch('clearDispatchQueue')
    },

    clearDispatchQueue ({ dispatch, commit }): void {
      dispatch('clearUpdatesPendingNotification')
      commit('CLEAR_DISPATCH_QUEUE')
    },
  },
}

export default RealtimeModule
export {
  RealtimeState,
}
